import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  IBiScreen,
  IBISettingsPermission,
  IGroupBody,
  IGroupUser,
  IPermissionGroup,
  IPermissionGroupDetails,
} from '@feature/settings/interfaces';
import { ApiEndpoints } from '@shared/common/apiPoint';
import { StandardResponse } from '@shared/interfaces/generic-response';
import { catchError, map, Observable, take, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GroupsPermissionService {
  private readonly _http = inject(HttpClient);

  getAllGroups(body = {}) {
    return this._http
      .post<
        StandardResponse<{ permissionGroups: IPermissionGroup[] }>
      >(ApiEndpoints.BIGroups.get_all_groups, body)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error in getting the grid data: ', error);
          return throwError(() => new Error(error.message));
        }),
        map(response => {
          if (response.status === 200 && response.data) {
            return response.data.permissionGroups;
          }
          return [];
        }),
      );
  }

  getGroupById(id: number) {
    return this._http
      .get<
        StandardResponse<IPermissionGroupDetails>
      >(`${ApiEndpoints.BIGroups.get_group_by_id}/${id}`)
      .pipe(take(1));
  }

  getAllUsers(body = {}) {
    return this._http
      .post<
        StandardResponse<{ permissionGroups: IGroupUser[] }>
      >(ApiEndpoints.BIGroups.get_groups_users, body)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error in getting the grid data: ', error);
          return throwError(() => new Error(error.message));
        }),
        map(response => {
          if (response.status === 200 && response.data) {
            return response.data.permissionGroups;
          }
          return [];
        }),
      );
  }

  getAllScreens() {
    return this._http
      .get<
        StandardResponse<{ screens: IBiScreen[] }>
      >(ApiEndpoints.BIScreens.get_all_screens)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error in getting the grid data: ', error);
          return throwError(() => new Error(error.message));
        }),
        map(response => {
          if (response.status === 200 && response.data) {
            return response.data.screens;
          }
          return [] as IBiScreen[];
        }),
      );
  }

  addUsersToGroups(body: { groupsIds: number[]; usersIds: number[] }) {
    return this._http
      .post<
        StandardResponse<any>
      >(`${ApiEndpoints.BIGroups.add_group_user}`, body)
      .pipe(take(1));
  }

  deleteUserFromGroup(userGroupId: number) {
    return this._http
      .delete<
        StandardResponse<any>
      >(`${ApiEndpoints.BIGroups.delete_user_group}/${userGroupId}`)
      .pipe(take(1));
  }

  toggleGroupUserStatus(userId: number, updatedById: number) {
    return this._http
      .put<
        StandardResponse<any>
      >(`${ApiEndpoints.BIGroups.update_user_group_status}/${userId}/${updatedById}`, {})
      .pipe(take(1));
  }

  toggleGroupStatus(groupId: number) {
    return this._http
      .put<
        StandardResponse<any>
      >(`${ApiEndpoints.BIGroups.update_group_status}/${groupId}`, {})
      .pipe(take(1));
  }

  deleteGroup(groupId: number) {
    return this._http
      .delete<
        StandardResponse<any>
      >(`${ApiEndpoints.BIGroups.delete_group}?id=${groupId}`)
      .pipe(take(1));
  }

  createNewGroup(body: IGroupBody) {
    return this._http
      .post<StandardResponse<any>>(`${ApiEndpoints.BIGroups.add_group}`, body)
      .pipe(take(1));
  }

  editPermissionsGroup(groupId: number, body: IGroupBody) {
    return this._http
      .put<
        StandardResponse<any>
      >(`${ApiEndpoints.BIGroups.edit_group}?id=${groupId}`, body)
      .pipe(take(1));
  }

  editUserGroups(body: { groupsIds: number[]; usersIds: number[] }) {
    return this._http
      .put<
        StandardResponse<any>
      >(`${ApiEndpoints.BIGroups.edit_user_groups}`, body)
      .pipe(take(1));
  }

  getBiPortalSettingsPermissions(
    accountId: number,
  ): Observable<IBISettingsPermission[]> {
    return this._http
      .get<
        StandardResponse<IBISettingsPermission[]>
      >(`${ApiEndpoints.BIScreens.get_bi_portal_settings}/${accountId}`)
      .pipe(
        take(1),
        map(response => response.data || []),
      );
  }
}
